import React, { memo } from "react";
import { Box } from "@mui/material";
import {
  GREY,
  BASE,
  BORDER,
  ERROR,
} from "../../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../../utils/constants/UI";
import IconComponent from "../../../../../components/Icon/IconComponent";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import { Button } from "../../../../../components/Buttons";
import CustomModal from "../../../../../components/Modals/CustomModal";
import { formatDate } from "./billingUtils";

/**
 * BillingCancelModal
 *
 * Cancellation confirmation dialog with impact summary and
 * typed-confirmation input.
 */
const BillingCancelModal = memo(
  ({
    open,
    onClose,
    isCancelling,
    cancelInfo,
    isTrialing,
    confirmCancelText,
    setConfirmCancelText,
    handleCancelSubscription,
  }) => {
    // Fix: use the SAME "plan" fallback that the displayed string + placeholder use, so when
    // plan_name is empty the matcher accepts exactly what the user is shown. (Was "" -> matcher only
    // accepted "cancel/" while the UI showed "cancel/plan" -> the confirm could never match -> the
    // user could never cancel.)
    const planSlug = (cancelInfo?.plan_name || "plan")
      .toLowerCase()
      .replace(/\s+/g, "-");
    const confirmCode = `cancel/${planSlug}`;
    const isConfirmed = confirmCancelText === confirmCode;

    const impactItems = [
      {
        icon: ICON_LABELS.SEND,
        label: "Active Campaigns",
        value: cancelInfo?.active_campaign_count?.toString() || "0",
        desc: "will stop at billing cycle end",
      },
      {
        icon: ICON_LABELS.SCHEDULE,
        label: "Service Ends",
        value: cancelInfo?.billing_cycle_end
          ? formatDate(cancelInfo.billing_cycle_end)
          : "-",
        desc: "your last day of access",
      },
    ];

    return (
      <CustomModal
        open={open}
        close={onClose}
        variant="error"
        header={{
          heading: {
            id: "cancel_sub",
            label: "Cancel Subscription",
          },
        }}
        width="520px"
        footer={{
          left: (
            <Box style={{ width: "fit-content" }}>
              <Button
                label="No, Keep My Plan"
                variant="ghost"
                size="sm36"
                onClick={onClose}
                disabled={isCancelling}
              />
            </Box>
          ),
          right: (
            <Box style={{ width: "fit-content" }}>
              <Button
                label="Yes, Cancel Subscription"
                variant="primary"
                size="sm36"
                backgroundColor={ERROR.W_600}
                onClick={handleCancelSubscription}
                isLoading={isCancelling}
                disabled={!isConfirmed || isCancelling}
              />
            </Box>
          ),
        }}
      >
        <Box>
          {/* Warning banner — paid subs only; a trial cancel shows nothing here. */}
          {!isTrialing && (
          <Box
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              padding: "14px 16px",
              background: ERROR.W_50,
              borderRadius: "8px",
              border: `1px solid ${ERROR.W_200}`,
              marginBottom: "20px",
            }}
          >
            <IconComponent
              iconLabel={ICON_LABELS.ERROR}
              style={{ color: ERROR.W_600, fontSize: "22px", marginTop: "1px" }}
            />
            <Box>
              <TypographyInline
                size="sm"
                fontWeight={fontWeight.SEMIBOLD}
                color={ERROR.W_700}
                label="What happens when you cancel"
              />
              <Box style={{ marginTop: "4px" }}>
                <TypographyInline
                  size="xs"
                  color={ERROR.W_600}
                  label={`Your ${cancelInfo?.plan_name || "plan"} stays active until the end of your current billing cycle. After that your workspace moves to the Free plan (credits reset to the free tier). You can reactivate any time before it ends.`}
                />
              </Box>
            </Box>
          </Box>
          )}

          {/* Impact summary */}
          <Box style={{ marginBottom: "20px" }}>
            <Box style={{ marginBottom: "10px" }}>
              <TypographyInline
                size="xs"
                fontWeight={fontWeight.BOLD}
                color={GREY.TERTIARY}
                label="CANCELLATION IMPACT"
              />
            </Box>
            <Box
              style={{
                borderLeft: `3px solid ${ERROR.W_400}`,
                borderRadius: "0 8px 8px 0",
                background: BASE.BACKGROUND_LIGHT,
                overflow: "hidden",
              }}
            >
              {impactItems.map((item, idx, arr) => (
                <Box
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 16px",
                    borderBottom:
                      idx < arr.length - 1
                        ? `1px solid ${BORDER.LIGHT}`
                        : "none",
                  }}
                >
                  <IconComponent
                    iconLabel={item.icon}
                    style={{ color: ERROR.W_400, fontSize: "20px" }}
                  />
                  <Box style={{ flex: 1 }}>
                    <Box
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <TypographyInline
                        size="sm"
                        fontWeight={fontWeight.MEDIUM}
                        color={GREY.PRIMARY}
                        label={item.label}
                      />
                      <TypographyInline
                        size="sm"
                        fontWeight={fontWeight.BOLD}
                        color={GREY.PRIMARY}
                        label={item.value}
                      />
                    </Box>
                    <TypographyInline
                      size="xs"
                      color={GREY.TERTIARY}
                      label={item.desc}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Confirmation input */}
          <Box
            style={{
              padding: "16px",
              background: ERROR.W_50,
              borderRadius: "8px",
              border: `1px dashed ${ERROR.W_200}`,
            }}
          >
            <Box style={{ marginBottom: "10px" }}>
              <TypographyInline
                size="sm"
                color={GREY.PRIMARY}
                fontWeight={fontWeight.MEDIUM}
                label="To verify, type "
              />
              <span
                onCopy={(e) => e.preventDefault()}
                style={{
                  fontFamily: "monospace",
                  fontSize: "13px",
                  background: ERROR.W_100,
                  color: ERROR.W_700,
                  padding: "2px 8px",
                  borderRadius: "4px",
                  fontWeight: 700,
                  userSelect: "none",
                  letterSpacing: "0.5px",
                }}
              >
                {confirmCode}
              </span>
              <TypographyInline
                size="sm"
                color={GREY.PRIMARY}
                fontWeight={fontWeight.MEDIUM}
                label=" below."
              />
            </Box>
            <input
              style={{
                width: "100%",
                border: isConfirmed
                  ? `2px solid ${ERROR.W_500}`
                  : `1px solid ${BORDER.DEFAULT}`,
                borderRadius: "6px",
                padding: "10px 14px",
                fontSize: "14px",
                fontFamily: "monospace",
                outline: "none",
                background: BASE.WHITE,
                color: GREY.PRIMARY,
                fontWeight: 600,
                letterSpacing: "0.5px",
                transition: "border-color 0.2s ease",
              }}
              placeholder={confirmCode}
              value={confirmCancelText}
              onChange={(e) => setConfirmCancelText(e.target.value)}
              onPaste={(e) => e.preventDefault()}
              disabled={isCancelling}
              autoComplete="off"
              spellCheck={false}
            />
          </Box>
        </Box>
      </CustomModal>
    );
  },
);

BillingCancelModal.displayName = "BillingCancelModal";

export default BillingCancelModal;
